import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { projectsApi, catalogApi, Project } from "../../api";
import { Settings, FileText, Save, Loader2, AlertCircle } from "lucide-react";
import clsx from "clsx";
import { ShellEditor } from "./ShellEditor";
import { PagesEditor } from "./PagesEditor";
import { ValidationPanel, ValidationBadge } from "./ValidationPanel";
import {
  validateConfig,
  countErrorsByPath,
  ValidationResult,
} from "../../utils/configValidator";

type EditorTab = "shell" | "pages";

interface ConfigEditorProps {
  project: Project;
  canEdit: boolean;
}

export function ConfigEditor({ project, canEdit }: ConfigEditorProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>("shell");
  const [config, setConfig] = useState(project.config);
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  // Carica il catalogo per la validazione
  const { data: catalogData } = useQuery({
    queryKey: ["catalog-current"],
    queryFn: () => catalogApi.getCurrent(),
  });

  const catalogContent = (catalogData?.data as any)?.data.catalog;

  // Validazione in tempo reale
  const validationResult: ValidationResult = useMemo(() => {
    const catalog =
      catalogContent ?? (Object.create(null) as Record<string, unknown>);
    // @ts-expect-error - TypeScript strict index signature check, catalog è garantito valido a runtime
    return validateConfig(config, catalog);
  }, [config, catalogContent]);

  // Conta errori per tab
  const shellErrors = countErrorsByPath(validationResult, "site.shell");
  const pagesErrors = countErrorsByPath(validationResult, "pages");

  const saveMutation = useMutation({
    mutationFn: (newConfig: typeof config) =>
      projectsApi.update(project.id, { config: newConfig }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", project.id] });
      setHasChanges(false);
    },
  });

  const handleConfigChange = (newConfig: typeof config) => {
    setConfig(newConfig);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(config);
  };

  // Sync config quando cambia il progetto esterno
  useEffect(() => {
    setConfig(project.config);
    setHasChanges(false);
  }, [project.config]);

  const tabs = [
    {
      id: "shell" as EditorTab,
      label: "Shell",
      icon: Settings,
      description: "Brand, navigazione, footer",
      errors: shellErrors.errors,
      warnings: shellErrors.warnings,
    },
    {
      id: "pages" as EditorTab,
      label: "Pagine",
      icon: FileText,
      description: "Struttura e contenuti",
      errors: pagesErrors.errors,
      warnings: pagesErrors.warnings,
    },
  ];

  return (
    <div className="card overflow-hidden">
      {/* Header con tabs */}
      <div className="border-b border-gray-200">
        <div className="px-6 py-4 flex items-center justify-between bg-gray-50">
          <h2 className="font-semibold text-gray-900">Editor configurazione</h2>
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className={clsx(
                "btn-primary text-sm",
                !hasChanges && "opacity-50 cursor-not-allowed",
              )}
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {hasChanges ? "Salva modifiche" : "Salvato"}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-t border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                "flex-1 px-4 py-3 text-sm font-medium transition-colors",
                "flex items-center justify-center space-x-2",
                activeTab === tab.id
                  ? "bg-white text-primary-600 border-b-2 border-primary-600"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100 border-b-2 border-transparent",
              )}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              <ValidationBadge errors={tab.errors} warnings={tab.warnings} />
            </button>
          ))}
        </div>
      </div>

      {/* Error message */}
      {saveMutation.isError && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200 flex items-center space-x-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">Errore nel salvataggio. Riprova.</span>
        </div>
      )}

      {/* Validation Panel */}
      <div className="px-6 pt-6">
        <ValidationPanel result={validationResult} />
      </div>

      {/* Tab content */}
      <div className="p-6">
        {activeTab === "shell" && (
          <ShellEditor
            config={config}
            onChange={handleConfigChange}
            canEdit={canEdit}
            catalog={catalogContent}
          />
        )}
        {activeTab === "pages" && (
          <PagesEditor
            config={config}
            onChange={handleConfigChange}
            canEdit={canEdit}
            catalog={catalogContent}
          />
        )}
      </div>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-amber-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Hai modifiche non salvate</span>
          </div>
          <button
            onClick={() => {
              setConfig(project.config);
              setHasChanges(false);
            }}
            className="text-sm text-amber-700 hover:text-amber-900 underline"
          >
            Annulla modifiche
          </button>
        </div>
      )}
    </div>
  );
}
